import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Snackbar } from '@mui/material';
import { useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import RecognitionDialog from '../components/RecognitionDialog';
import api from '../api/api';

interface Recognition {
  name: string;
  box: [number, number, number, number];
  student_id: number;
  profile_picture?: string;
  event_id: number; // Recognition Event ID
  attendance_created: boolean;
}

const LiveView: React.FC = () => {
  const { cameraId } = useParams<{ cameraId: string }>();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentRecognition, setCurrentRecognition] = useState<Recognition | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean, message: string }>({ open: false, message: '' });

  // To prevent spamming dialogs, keep track of recently shown students
  const recentlyVerified = useRef(new Set<number>());

  const handleDialogClose = useCallback(async (confirmed: boolean) => {
    if (currentRecognition) {
      try {
        await api.patch(`/events/${currentRecognition.event_id}/review`, {
          status: confirmed ? 'confirmed' : 'rejected',
          student_id: currentRecognition.student_id,
        });
        setSnackbar({ open: true, message: `Recognition for ${currentRecognition.name} has been ${confirmed ? 'confirmed' : 'rejected'}.` });
      } catch (err) {
        console.error('Failed to update review status:', err);
        setSnackbar({ open: true, message: 'Failed to submit review.' });
      } finally {
        // Add to recently verified to prevent immediate re-opening of dialog
        recentlyVerified.current.add(currentRecognition.student_id);
        setTimeout(() => {
            recentlyVerified.current.delete(currentRecognition.student_id);
        }, 30000); // Cooldown period of 30 seconds
      }
    }
    setDialogOpen(false);
    setCurrentRecognition(null);
  }, [currentRecognition]);

  useEffect(() => {
    console.log('LiveView cameraId:', cameraId);
    if (!cameraId) {
      setError('Camera ID is missing from the URL.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
        setError("Authentication token not found.");
        return;
    }
    
    const wsUrl = `ws://localhost:8000/cameras/ws/${cameraId}?token=${token}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('WebSocket connection established');
      setError(null);
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;

      const image = new Image();
      image.src = `data:image/jpeg;base64,${data.frame}`;
      image.onload = () => {
        canvas.width = image.width;
        canvas.height = image.height;
        ctx.drawImage(image, 0, 0);

        if (data.recognitions && data.recognitions.length > 0) {
            data.recognitions.forEach((rec: Recognition) => {
              const [x, y, w, h] = rec.box;
              ctx.strokeStyle = 'lime';
              ctx.lineWidth = 2;
              ctx.strokeRect(x, y, w, h);
  
              ctx.fillStyle = 'lime';
              ctx.font = '18px Arial';
              ctx.fillText(rec.name, x, y > 10 ? y - 5 : 10);
            });

            // Handle the first recognition for verification
            const firstRec = data.recognitions[0];
            if (firstRec.student_id && !dialogOpen && !recentlyVerified.current.has(firstRec.student_id)) {
                if (firstRec.attendance_created) {
                    setCurrentRecognition(firstRec);
                    setDialogOpen(true);
                } else {
                    // If attendance was already recorded, show a snackbar instead of dialog
                    setSnackbar({ open: true, message: `${firstRec.name} has already been marked present today.` });
                    // Add to recently verified to prevent spamming snackbar
                    recentlyVerified.current.add(firstRec.student_id);
                    setTimeout(() => {
                        recentlyVerified.current.delete(firstRec.student_id);
                    }, 5000); // Shorter cooldown for snackbar
                }
            }
          }
      };
    };

    ws.onerror = (err) => {
      console.error('WebSocket error:', err);
      setError("Connection to the live feed failed. Please ensure the camera is active and the backend is running.");
    };

    ws.onclose = () => {
      console.log('WebSocket connection closed');
    };

    return () => {
      ws.close();
    };
  }, [cameraId, dialogOpen]);

  return (
    <Box>
      <PageHeader title={`Live View - Camera ${cameraId}`} />
      <Box sx={{
        mt: 2,
        bgcolor: 'black',
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '480px'
      }}>
        {error ? (
            <Box sx={{color: 'white', p: 2}}>{error}</Box>
        ) : (
            <canvas ref={canvasRef} style={{ maxWidth: '100%', height: 'auto' }} />
        )}
      </Box>
      <RecognitionDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        recognition={currentRecognition}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default LiveView; 